<script setup lang="ts">
import { computed } from 'vue'
import { useLocalStorage } from '#imports'
import { useDeliveryCountryNoticeQuery } from '~~/app/composables/queries/useStoreApi'

/**
 * Baner „Dostarczamy przesyłkę do Twojego kraju: Francja" dla odwiedzających z zagranicy
 * (Asana 1218980517635240). Kraj z Cloudflare (`CF-IPCountry`), lista krajów dostawy
 * z przewoźników InPost International — baner pojawia się tylko tam, gdzie realnie jeździmy.
 *
 * Renderowany WYŁĄCZNIE po stronie klienta (SiteHeader owija go w <ClientOnly>): HTML sklepu
 * jest cache'owany na krawędzi, więc kraj wstawiony w SSR zobaczyliby wszyscy odwiedzający.
 * Druga linia po angielsku, bo gość z Francji nie musi czytać po polsku.
 */
const brand = useBrand()
const route = useRoute()
const { t } = useI18n()

const preview = computed(() => {
  const value = route.query.delivery_country_preview
  return typeof value === 'string' ? value : ''
})

const { data: notice } = useDeliveryCountryNoticeQuery(
  computed(() => Boolean(brand.deliveryCountryBanner)),
  preview,
)

// Zamknięcie zapamiętane PER KRAJ — klient, który zamknął baner we Francji, nie zobaczy go
// przy każdym wejściu, ale zobaczy po przeprowadzce do Belgii.
const dismissedCountries = useLocalStorage<string[]>('lavmag.deliveryCountryBanner.dismissed', [])

const country = computed(() => notice.value?.country ?? '')
const visible = computed(() =>
  Boolean(notice.value?.delivers && country.value && notice.value?.name)
  && !dismissedCountries.value.includes(country.value),
)

const englishName = computed(() => {
  if (!country.value) return ''
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(country.value) ?? country.value
  }
  catch {
    return country.value
  }
})

function dismiss() {
  if (!country.value) return
  dismissedCountries.value = [...new Set([...dismissedCountries.value, country.value])]
}
</script>

<template>
  <div
    v-if="visible"
    class="delivery-country-banner"
    role="status"
    data-testid="delivery-country-banner"
  >
    <p class="delivery-country-banner__text">
      <span>{{ t('internationalShipping.banner') }} <strong>{{ notice?.name }}</strong></span>
      <span class="delivery-country-banner__en" lang="en">{{ t('internationalShipping.bannerEnglish', { country: englishName }) }}</span>
    </p>
    <button
      type="button"
      class="delivery-country-banner__close"
      :aria-label="t('internationalShipping.closeBanner')"
      data-testid="delivery-country-banner-close"
      @click="dismiss"
    >
      ×
    </button>
  </div>
</template>

<style scoped>
.delivery-country-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 8px 48px 8px 16px;
  position: relative;
  background: var(--color-black);
  color: var(--color-white);
  font-size: 0.875rem;
  line-height: 1.35;
  text-align: center;
}

.delivery-country-banner__text {
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  column-gap: 12px;
  row-gap: 2px;
}

.delivery-country-banner__en {
  opacity: 0.75;
}

.delivery-country-banner__close {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  border: 0;
  background: transparent;
  color: inherit;
  font-size: 1.25rem;
  line-height: 1;
  cursor: pointer;
}

.delivery-country-banner__close:hover {
  opacity: 0.7;
}
</style>
